import React, { useEffect, useState } from "react"
import Pagination from "../../components/Pagination"
import { FiPlusCircle, FiEye } from "react-icons/fi"
import { SuccessNotification, ErrorNotification } from "../../utils"
import { Redirect, useLocation } from "react-router-dom"
import DashboardHeader from "../../components/DashboardHeader"
import { Tooltip } from "../../layout/sidebar"
import { IoArrowBack, IoArrowForward } from "react-icons/io5"
import EmptyHarvestRequest from "../../components/NoData/EmptyHarvestRequest"
import AddHarvestRequestModal from "../../components/modals/AddHarvestRequestModal"
import HarvestRequestFilter from "../../components/Filters/HarvestRequestFilter"
import Spinner from "../../components/Spinner"
import useSWR from "swr"
import { BASE_URL, fetchAll, fetcher } from "../../utils/api"
import { RxPencil1 } from "react-icons/rx"
import EditHarvestRequestModal from '../../components/modals/EditHarvestRequestModal'
import DeleteModal from "../../components/DeleteModal"
import { useHistory } from "react-router-dom";
import {
  formatHarvestEstimatedValue,
  formatHarvestPriceDisplay,
  formatHarvestQuantityDisplay,
} from "../../utils/harvestRequest"

const PAGE_SIZE = 10

const Index = () => {
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1)
  const [selected, setSelected] = useState(null)
  const [showDetailDrawer, setShowDetailDrawer] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [q, setQ] = useState("")
  const [states, setState] = useState({ status: "", request_type: "", date: "" })
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection"
    }
  ])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { data, error, isLoading, mutate } = useSWR(
    `harvest-requests/`,
    () => fetchAll(`harvest-requests/`)
  )

  // --- Filtering and Search Logic ---
  const getFilteredHarvests = () => {
    let harvests = data || []
    if (states.status && states.status !== "") {
      harvests = harvests.filter(
        (el) => el.request_status?.toLowerCase() === states.status?.toLowerCase()
      )
    }
    if (states.request_type && states.request_type !== "") {
      harvests = harvests.filter(
        (el) => el.request_type?.toLowerCase() === states.request_type?.toLowerCase()
      )
    }
    if (q) {
      const searchTerm = q.toLowerCase()
      harvests = harvests.filter(
        (el) =>
          el.title?.toLowerCase()?.includes(searchTerm) ||
          el.request_id?.toLowerCase()?.includes(searchTerm) ||
          el.commodity?.name?.toLowerCase()?.includes(searchTerm) ||
          `${el.owner?.firstname} ${el.owner?.lastname}`
            ?.toLowerCase()
            ?.includes(searchTerm)
      )
    }
    return harvests
  }

  const harvestRequest = getFilteredHarvests()

  // --- Suggestions for Search ---
  const suggestions = (harvestRequest || [])
    .flatMap((h) => [
      h.title,
      h.request_id,
      h.commodity?.name,
      `${h.owner?.firstname} ${h.owner?.lastname}`,
    ])
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i)

  const formatStatusLabel = (status) =>
    status ? status.replace(/_/g, " ") : "N/A"

  const getRequestStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
      case "published":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
      case "pending":
      case "draft":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
      case "rejected":
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
    }
  }

  const getResponseStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "accepted":
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
    }
  }

  const getAudienceLabel = (request) => {
    if (request?.to_all_farmer) return "All farmers"
    const groupCount = request?.target_groups_count ?? 0
    return groupCount === 1 ? "1 group" : `${groupCount} groups`
  }

  const formatDate = (date) => {
    if (!date) return "N/A"
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  }

  const { state } = useLocation()
  const user = JSON.parse(localStorage.getItem("user"))
  
  // Pagination
  const totalPages = Math.ceil((harvestRequest?.length || 0) / PAGE_SIZE)
  const currentItems = (harvestRequest || []).slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const resetFilters = () => {
    setQ("")
    setState({ status: "", request_type: "", date: "" })
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection"
      }
    ])
    setCurrentPage(1)
  }

  function handleChange(evt) {
    const value = evt.target.value
    setState({
      ...states,
      [evt.target.name]: value,
    })
    setCurrentPage(1)
  }

  // Delete handler
  const handleDelete = async () => {
    if (!selected?.id) return
    setDeleteLoading(true)
    try {
      const token = localStorage.getItem('token')
      await fetch(`${BASE_URL}harvest-requests/${selected.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      SuccessNotification("Harvest request deleted")
      setShowDeleteModal(false)
      mutate()
    } catch (error) {
      ErrorNotification("Failed to delete harvest request")
    } finally {
      setDeleteLoading(false)
    }
  }

  if (user?.is_firsttime) {
    return <Redirect to={state?.from || "/admin/dashboard"} />
  }

  return (
    <>
      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="Harvest Requests" />
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 rounded-md"
            >
              <FiPlusCircle size={20} />
              Add harvest request
            </button>
          </div>

          <div className="flex flex-col gap-4">
            <HarvestRequestFilter
              onSearch={setQ}
              searchValue={q}
              setSearchValue={setQ}
              suggestions={suggestions}
              states={states}
              handleChange={handleChange}
              refreshFilter={resetFilters}
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          </div>

          {isLoading ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="my-5">
              {harvestRequest?.length > 0 ? (
                <div className="flex flex-col">
                  <div className="-m-1.5 overflow-x-auto">
                    <div className="p-1.5 min-w-full inline-block align-middle">
                      <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                          <thead className="bg-transparent dark:bg-secondaryDark">
                            <tr>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Title</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Owner</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Commodity</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Quantity</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Price / Unit</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Est. Value</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Audience</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Status</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Response</th>
                              <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Created</th>
                              <th className="px-6 py-3 text-end text-sm font-light text-gray-500 capitalize">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-border-dark bg-white dark:bg-dark">
                            {currentItems.map((h) => (
                              <tr key={h.id} className="text-[13px]">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="font-medium text-gray-900 dark:text-white capitalize">
                                    {h.title || "Untitled request"}
                                  </div>
                                  {h.request_id && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 uppercase">
                                      {h.request_id}
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap capitalize">
                                  {h.owner?.firstname || h.owner?.lastname
                                    ? `${h.owner?.firstname || ""} ${h.owner?.lastname || ""}`.trim()
                                    : h.owner?.email || "N/A"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap capitalize">
                                  {typeof h.commodity === "object" ? h.commodity?.name : h.commodity || "N/A"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {formatHarvestQuantityDisplay(h)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {formatHarvestPriceDisplay(h)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {formatHarvestEstimatedValue(h)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap capitalize">
                                  {getAudienceLabel(h)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap capitalize">
                                  <span className={`px-2 py-1 rounded-full text-xs ${getRequestStatusClass(h.request_status)}`}>
                                    {formatStatusLabel(h.request_status)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap capitalize">
                                  <span className={`px-2 py-1 rounded-full text-xs ${getResponseStatusClass(h.response_status)}`}>
                                    {formatStatusLabel(h.response_status)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {formatDate(h.created_at)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-end">
                                  <div className="flex flex-row items-center justify-end gap-3">
                                    <Tooltip text="View" position="top">
                                      <button onClick={() => history.push(`/admin/harvest-requests/${h.id || h.request_id}`, { state: { state: h } })}>
                                        <FiEye size={16} />
                                      </button>
                                    </Tooltip>
                                    <Tooltip text="Edit" position="top">
                                      <button onClick={() => { setEditId(h.id || h.request_id); setShowEditModal(true) }}>
                                        <RxPencil1 size={16} />
                                      </button>
                                    </Tooltip>
                                    <Tooltip text="Delete" position="top">
                                      <button onClick={() => { setSelected(h); setEditId(h.id || h.request_id); setShowDeleteModal(true) }}>
                                        <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </button>
                                    </Tooltip>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyHarvestRequest />
              )}
            </div>
          )}
        </div>
      </div>

      {/* HarvestDetailDrawer removed in favor of HarvestDetailView navigation */}

      {showAddModal && (
        <AddHarvestRequestModal
          showAddModal={showAddModal}
          setShowAddModal={setShowAddModal}
          mutate={mutate}
        />
      )}

      {showEditModal && (
        <EditHarvestRequestModal
          show={showEditModal}
          setShow={setShowEditModal}
          harvestId={editId}
          mutate={mutate}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          showConfirmModal={showDeleteModal}
          setShowConfirmModal={setShowDeleteModal}
          handleDelete={handleDelete}
          loading={deleteLoading}
          title="harvest request"
        />
      )}
    </>
  )
}

export default Index